import { IsNotEmpty, IsString, IsOptional, IsUUID, MaxLength, IsInt, IsBoolean, IsEnum, Min, IsDateString, Matches } from 'class-validator';
import { Schedule, ProductStatus } from '../../../enums';

export class CreateProductRequestDto {
    /**
     * Official name of the product
     * @example 'MediProduct'
     */
    @IsNotEmpty()
    @IsString()
    @MaxLength(255)
    productName: string;

    /**
     * Alternate name or research code for the product
     * @example 'MPD-123'
     */
    @IsOptional()
    @IsString()
    @MaxLength(255)
    alternateName?: string;

    /**
     * Product item code from MPD
     * @example '7751-1550-000'
     */
    @IsOptional()
    @IsString()
    @MaxLength(100)
    productItemCode?: string;

    /**
     * Fabricator product code
     * @example '7751-0240-231'
     */
    @IsOptional()
    @IsString()
    @MaxLength(256)
    fabricatorProductCode?: string;

    @IsOptional()
    @IsUUID()
    clientInformationRecordId?: string;

    @IsOptional()
    @IsString()
    @MaxLength(256)
    mraStatus?: string;

    @IsOptional()
    @IsString()
    @MaxLength(256)
    picsStatus?: string;

    @IsOptional()
    @IsString()
    uicDescription?: string;

    @IsOptional()
    @IsDateString()
    uicExpiry?: string;

    @IsOptional()
    @IsBoolean()
    qcGvpResponsibleForPv?: boolean;

    @IsOptional()
    @IsBoolean()
    qcRegulatoryResponsibleForProductLifecycle?: boolean;

    /**
     * Product size (free text, e.g., "500 mL", "30 tablets")
     * @example '500 mL'
     */
    @IsOptional()
    @IsString()
    @MaxLength(255)
    size?: string;

    /**
     * Product dosage form
     * @example 'Tablet'
     */
    @IsOptional()
    @IsString()
    @MaxLength(30)
    @Matches(/^[a-zA-Z0-9 .%()\-]*$/)
    dosageForm?: string;

    /**
     * Drug Identification Number
     * @example '02242893'
     */
    @IsOptional()
    @IsString()
    @MaxLength(50)
    din?: string;

    /**
     * Natural Product Number
     * @example '80025550'
     */
    @IsOptional()
    @IsString()
    @MaxLength(50)
    npn?: string;

    /**
     * List of active ingredients and strengths
     * @example 'Lactic acid 10%, Urea 20%'
     */
    @IsOptional()
    @IsString()
    activeIngredients?: string;

    /**
     * Dosage strength / concentration
     * @example '80mg/ml (200 mg/2.5 mL)'
     */
    @IsOptional()
    @IsString()
    dosageStrength?: string;

    /**
     * Shelf life in months
     * @example 30
     */
    @IsOptional()
    @IsInt()
    @Min(0)
    shelfLifeMonths?: number;

    /**
     * Product schedule
     * @example 'OTC'
     */
    @IsOptional()
    @IsEnum(Schedule)
    schedule?: Schedule;

    /**
     * Storage conditions as per label
     * @example '2 to 25°C. Product can be refrigerated'
     */
    @IsOptional()
    @IsString()
    @MaxLength(255)
    labelledStorageConditions?: string;

    /**
     * Marketing Authorization Holder organization ID
     * @example 'a1b2c3d4-e5f6-7890-1234-567890abcdef'
     */
    @IsOptional()
    @IsUUID()
    mahOrganizationId?: string;

    /**
     * Product status
     * @example 'DISCONTINUED'
     */
    @IsOptional()
    @IsEnum(ProductStatus)
    discontinuationStatus?: ProductStatus;

    /**
     * Product status notes
     * @example 'Product discontinued as of Q2 2024 due to regulatory changes'
     */
    @IsOptional()
    @IsString()
    discontinuationNotes?: string;

    /**
     * Universal Product Code or GTIN
     * @example '735005284376'
     */
    @IsOptional()
    @IsString()
    @MaxLength(50)
    upcGtin?: string;

    /**
     * Common batch sizes for this product
     * @example '550 kg, 800 kg, 1600 kg'
     */
    @IsOptional()
    @IsString()
    batchSizes?: string;

    /**
     * Details if product is on hold
     * @example 'ON HOLD OCT 7, 2015 at warehouse'
     */
    @IsOptional()
    @IsString()
    onHoldDetails?: string;

    /**
     * Flag if product entry refers to reworked material
     * @example false
     */
    @IsOptional()
    @IsBoolean()
    isReworked?: boolean;

    /**
     * Details if reworked
     * @example 'Reworked at Promag'
     */
    @IsOptional()
    @IsString()
    reworkDetails?: string;

    /**
     * Reference to original product if this is a rework
     * @example 'a1b2c3d4-e5f6-7890-1234-567890abcdef'
     */
    @IsOptional()
    @IsUUID()
    originalProductId?: string;

    /**
     * Notes on Health Canada approval and launch dates
     * @example 'HC approval received March 2023, launched May 2023'
     */
    @IsOptional()
    @IsString()
    approvalLaunchNotes?: string;

    /**
     * General comments field
     * @example 'Product needs to be shipped under controlled conditions'
     */
    @IsOptional()
    @IsString()
    generalComments?: string;

    /**
     * User who last updated this record
     * @example 'John Smith'
     */
    @IsOptional()
    @IsString()
    @MaxLength(100)
    lastUpdatedBy?: string;
}
